import { useLocation, useNavigate } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { ApolloError, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { GET_ALL_SKILLS, GET_IDEA_GOAL, GOAL_MASTER_DATA, PROJECTS_BY_EMP_ID } from "../../api/query";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../context/AuthContext";
import alertIcon from "../../assets/dropdown/alert-icon-error.svg";
import InputsDropdownMultipleSearchSelect from "../../components/common/InputsDropdownMultipleSearchSelect";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import InputsNew from "../../components/common/InputsNew";
import viewdeleteIon from "../../assets/task/delete_viewidea4icons.svg";
import viewFileIon from "../../assets/task/vewidea-iconfile.svg";
import { uploadFileDirectly } from "../projects/AddProject";
import ioniconaaddoutline from "../../assets/dashboard/ioniconaaddoutline.svg";
import CheckIcon from "../../assets/task/check.png";
import { Plus } from "lucide-react";
import { CREATE_IDEA_FOLLOWERS, DELETE_IDEA_FOLLOWER, UPDATE_IDEA_GOAL } from "../../api/mutation";
import DateSelector from "../../components/common/DateSelector";
import { LinkItem, LinkManager } from "../../components/customerSuccess/LinkManager";
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";

const priorityOptions = [
    {
        key: 1,
        name: "Low"
    },
    {
        key: 2,
        name: "Medium"
    },
    {
        key: 3,
        name: "High"
    }
]

interface ViewIdeaConfig {
    projectName: string
    projectID: string
    goalFor: string
    goalType: string
    goalID: string
    goalName: string
    goalDescription: string
    priority: string
    expectedEndDate: string
}

// Validation schema
const viewIdeaSchema = yup.object().shape({
    projectName: yup.string().required('This field is required'),
    projectID: yup.string().required('This field is required'),
    goalFor: yup.string().required('This field is required'),
    goalType: yup.string().required('This field is required'),
    goalID: yup.string().required('This field is required'),
    goalName: yup.string()
        .test('not-only-whitespace', 'Please add a valid Goal Name', value => !value || value.trim().length > 0)
        .required('This field is required'),
    goalDescription: yup.string()
        .test('not-only-whitespace', 'Please add a valid Goal Name', value => !value || value.trim().length > 0)
        .required('This field is required'),
    priority: yup.string().required('This field is required'),
    expectedEndDate: yup.string().required('This field is required'),
});

const ViewIdea = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const { userInfo } = useAuth()

    const location = useLocation()
    const state = location.state as {
        ideaID: string
    };

    const { ideaID } = state

    const employeeID = userInfo?.employee_id || '';
    const organizationID = userInfo?.agencies?.id || '';

    const [projectOptions, setProjectOptions] = useState<{ key: string; name: string }[]>([]);
    const [goalTypeOptions, setGoalTypeOptions] = useState<{ key: string; name: string }[]>([]);
    const [clearSelected, setClearSelected] = useState(false);
    const [ideaAttachmentView, setIdeaAttachmemntView] = useState("attachments");
    const [imgUrls, setImgUrls] = useState<string[]>([]);
    const [fileNames, setFileNames] = useState<string[]>([]);
    const [fileTypes, setFileTypes] = useState<string[]>([]);
    const [existingAttachments, setExistingAttachments] = useState<
        { id: string; file_path: string; file_name: string; file_type: string }[]
    >([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);
    const [isFollowed, setIsFollowed] = useState(false)
    const [links, setLinks] = useState<LinkItem[]>([]);
    const [skillOptions, setSkillOptions] = useState<
        { key: string; name: string }[]
    >([]);
    const [selectedSkills, setSelectedSkills] = useState<any[]>([]);

    //api
    const { data: skillData, loading: skillLoading } = useQuery(GET_ALL_SKILLS, {
        fetchPolicy: 'no-cache',
    });

    const [updateIdea, { loading: updateIdeaLoading }] = useMutation(UPDATE_IDEA_GOAL, {
        fetchPolicy: 'no-cache',
    })

    const [createFollower, { loading: createFollowerLoading }] = useMutation(CREATE_IDEA_FOLLOWERS, {
        fetchPolicy: 'no-cache',
    })

    const [deleteFollower, { loading: deleteFollowerLoading }] = useMutation(DELETE_IDEA_FOLLOWER, {
        fetchPolicy: 'no-cache',
    })

    const [
        getProjectsByEmpID,
        { data: projectsData, loading: projectsDataLoading },
    ] = useLazyQuery(PROJECTS_BY_EMP_ID, {
        fetchPolicy: 'no-cache'
    });

    const [
        getGoalMasterData,
        { data: goalsData, loading: goalsDataLoading },
    ] = useLazyQuery(GOAL_MASTER_DATA, {
        fetchPolicy: 'no-cache'
    });

    const [
        getIdea,
        { data: ideaData, loading: ideaDataLoading },
    ] = useLazyQuery(GET_IDEA_GOAL, {
        fetchPolicy: 'no-cache'
    });


    //react-hook form
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        watch,
        control,
        getValues
    } = useForm<ViewIdeaConfig>({
        resolver: yupResolver(viewIdeaSchema),
        defaultValues: {
            goalFor: "Product Development"
        }
    });

    //watch
    const watchProjectName = watch('projectName')
    const watchGoalType = watch('goalType')
    const watchGoalFor = watch('goalFor')
    const watchPriority = watch('priority')

    //useEffects
    useEffect(() => {
        if (skillData && skillData.get_all_skills?.skills?.length > 0) {
            const options = skillData.get_all_skills?.skills?.map((skill: any) => ({
                key: skill.id,
                name: skill?.skill_name
            }));
            setSkillOptions(options);
        }
    }, [skillData])

    useEffect(() => {
        if (employeeID) {
            getProjectsByEmpID({
                variables: {
                    emp_id: employeeID,
                    organization_id: organizationID
                },
            });
        }
    }, [employeeID])

    useEffect(() => {
        if (projectsData && projectsData.projects_by_emp_id) {
            const projects = projectsData?.projects_by_emp_id || [];
            const options = projects.map((p: any) => ({
                key: p.id,
                name: p.name
            }));
            setProjectOptions(options);
        }
    }, [projectsData]);

    useEffect(() => {
        if (watchGoalFor) {
            getGoalMasterData({
                variables: {
                    goal_for: watchGoalFor,
                }
            })
        }
    }, [watchGoalFor])

    useEffect(() => {
        if (goalsData?.goal_master_data?.goals_static) {
            const goals = goalsData.goal_master_data.goals_static.map((goal: any) => ({
                key: goal.id,
                name: goal.name,
                type: goal?.outcome
            }));
            setGoalTypeOptions(goals);
        }
    }, [goalsData]);

    useEffect(() => {
        if (ideaID) {
            getIdea({
                variables: {
                    id: parseInt(ideaID)
                },
            });
        }
    }, [ideaID])

    useEffect(() => {
        if (ideaData && ideaData?.get_idea_goal?.status_code == 200) {
            const idea = ideaData?.get_idea_goal?.idea_goal

            setValue('projectName', idea?.project_name);
            setValue('projectID', idea?.project_id);
            setValue('goalType', idea?.goals_static_name);
            setValue('goalID', idea?.goals_static_id);
            setValue('goalName', idea?.title)
            setValue('goalDescription', idea?.user_input);
            setValue('priority', idea?.priority);
            setValue('expectedEndDate', idea?.expected_closing_date)
            setValue('goalFor', idea?.goal_for)

            if (idea?.attachments?.length > 0) {
                const attachments = idea.attachments;
                const fileUrls = attachments.map((attachment: any) => attachment.file_path);
                const fileNames = attachments.map((attachment: any) => attachment.file_name);
                const fileTypes = attachments.map((attachment: any) => attachment.file_type);

                // Set the state with the extracted data
                setImgUrls(fileUrls);
                setFileNames(fileNames);
                setFileTypes(fileTypes)

                setExistingAttachments(attachments.map((a: any) => ({
                    id: a.id,
                    file_path: a.file_path,
                    file_name: a.file_name,
                    file_type: a.file_type
                })));
            }

            if (idea?.links?.length > 0) {
                setLinks(idea?.links?.map((l: any) => ({
                    id: Date.now().toString(),
                    url: l
                })));
            }

            if (idea?.goal_followers?.length > 0) {
                // Loop through each follower
                idea.goal_followers.forEach((follower: any) => {
                    if (follower.follower_user_id == employeeID) {
                        setIsFollowed(true);
                    } else {
                        setIsFollowed(false)
                    }
                });
            } else {
                setIsFollowed(false)
            }

            const skillOptions = idea?.skills?.map((s: any) => ({
                key: s?.skill_id,
                name: s?.skill_name
            }));

            setSelectedSkills(skillOptions)
        }
    }, [ideaData])

    function handleSelectProject(key: string, name: string): void {
        setValue('projectID', key)
        setValue('projectName', name)
        trigger('projectName')

        // Find the selected project
        // const selectedProject = projectsData?.projects_by_emp_id.find((project: any) => project.id === key);

        // setValue('goalName', '');
        // setValue('goalID', '');
        // setValue('existingGoalID', '');
        // setGoalNameOptions([]);
        // setValue('startGoalDate', null);
        // setValue('endGoalDate', null);
        // setValue('goalType', '');
        // setClearSelected(true);
        // setClearGoalSelected(true)
        // setGoalState("existing")
    }

    function handleGoalType(key: string, name: string): void {
        // setClearGoalSelected(false);
        setValue('goalID', key)
        setValue('goalType', name)
        trigger('goalType')
    }

    function handlePriority(key: string, name: string): void {
        // setClearGoalSelected(false);
        // setValue('goalID', key)
        setValue('priority', name)
        trigger('priority')
    }

    function handleIconClick() {
        fileInputRef.current?.click();
    }

    async function handleFileUpload(event: ChangeEvent<HTMLInputElement>) {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const token = localStorage.getItem("authToken")!;
        const validTypes = ["image/jpeg", "image/png"];
        // const newImageUrls: string[] = [];
        const newUrls: string[] = [];
        const newNames: string[] = [];
        const newTypes: string[] = [];

        setIsUploading(true);
        setValidationMessage(null);

        for (const file of Array.from(files)) {
            // if (!validTypes.includes(file.type)) {
            //     setValidationMessage("Only JPG and PNG files are allowed.");
            //     setIsUploading(false);
            //     return;
            // }

            try {
                const url = await uploadFileDirectly(file, file.name, token);
                newUrls.push(url);
                newNames.push(file.name);
                newTypes.push(file.type)
            } catch (err) {
                console.error("Upload failed", err);
            }
        }

        // setImgUrls((prev) => [...prev, ...newImageUrls]);
        setImgUrls((prev) => [...prev, ...newUrls]);
        setFileNames((prev) => [...prev, ...newNames]);
        setFileTypes((prev) => [...prev, ...newTypes]);
        setIsUploading(false);

        // If needed, set values in your form for each image (optional)
        // e.g., setValue("imageUrls", [...imgUrls, ...newImageUrls])
    }

    const handleSelectSkills = (key: any, name: any, type: any) => {
        const allOptions = skillOptions;

        if (key === "all") {
            if (name === true) {
                // Deselect all
                setSelectedSkills([]);
            } else {
                // Select all
                setSelectedSkills(allOptions);
            }
        } else {
            setSelectedSkills((prev) => {
                const exists = prev.some((item) => item.key == key);
                if (exists) {
                    // REMOVE if already selected
                    return prev.filter((item) => item.key != key);
                } else {
                    // ADD if not selected
                    return [...prev, { key, name, type }];
                }
            });
        }
    };

    const handleRemoveSkill = (key: string) => {
        setSelectedSkills((prev) => prev.filter((item) => item.key !== key));
    };

    async function handleFollowClick() {
        try {
            const response = await createFollower({
                variables: {
                    employee_ids: [parseInt(employeeID)],
                    idea_goal_id: parseInt(ideaID)
                }
            });
            if (response.data.create_idea_followers.status_code === 200) {
                showSuccess(response.data.create_idea_followers.message)

                await getIdea({
                    variables: {
                        id: parseInt(ideaID)
                    },
                });
            }
            if (response.data.create_idea_followers.status_code === 400) {
                showError(response.data.create_idea_followers.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    async function handleUnFollowClick() {
        try {
            const response = await deleteFollower({
                variables: {
                    employee_ids: [parseInt(employeeID)],
                    idea_goal_id: parseInt(ideaID)
                }
            });
            if (response.data.delete_idea_followers.status_code === 200) {
                showSuccess(response.data.delete_idea_followers.message)

                await getIdea({
                    variables: {
                        id: parseInt(ideaID)
                    },
                });
            }
            if (response.data.delete_idea_followers.status_code === 400) {
                showError(response.data.delete_idea_followers.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    async function onSubmit(data: ViewIdeaConfig, mode: "update" | "convert") {
        if (selectedSkills?.length < 1) {
            showError('Please Select Atleast One Skill')
            return
        }

        const skillIds = selectedSkills?.length > 0 ? selectedSkills?.map((s: any) => {
            return parseInt(s?.key)
        }) : []

        const existingUrls = new Set(existingAttachments.map((att) => att.file_path));

        const newAttachmentList = imgUrls
            ?.map((url, index) => ({
                file_name: fileNames[index],
                file_path: url,
                file_type: fileTypes[index]
            }))
            .filter((item) => !existingUrls.has(item.file_path));

        const urls = links.map(l => l.url);

        const payload = {
            id: parseInt(ideaID),
            // user_input: data?.title,
            title: data?.goalName,
            project_id: parseInt(data?.projectID),
            goal_for: data?.goalFor,
            goals_static_id: parseInt(data?.goalID),
            expected_closing_date: data?.expectedEndDate,
            links: urls,
            // outcome_id: parseInt(selectedOutcomeID),
            priority: data?.priority,
            // user_selected: data.criticality === "No" ? false : true,
            // impact_level_id: parseInt(data?.impactLevelID),
            attachment_list: newAttachmentList || [],
            skill_ids: skillIds,
            updated_by: parseInt(employeeID)
        }

        try {
            const response = await updateIdea({ variables: { input: payload } });
            if (response.data.update_idea_goal.status_code === 200) {
                showSuccess(response.data.update_idea_goal.message)
                // navigate('/ideas')

                // navigate based on which button was clicked
                if (mode === "update") {
                    navigate('/ideas');
                } else if (mode === "convert") {
                    navigate('/add_task', { state: { ideaID } });
                }
            }
            if (response.data.update_idea_goal.status_code === 400) {
                showError(response.data.update_idea_goal.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    return (
        <>
            <form id="cts-respon-p-dv" className="view-idea-section-dv w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope">
                <div className="flex-1 flex flex-col items-start justify-start gap-6">
                    <BreadCumb />
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">View Idea</h3>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
                        <div className="flex  items-center w-full justify-between">
                            <h5 className="flex-1 flex my-0 text-xl-dv self-stretch relative text-lg">Goal Details</h5>
                            {isFollowed == true ? (
                                <div className="flex-1 flex  grid flex-row items-center justify-end  box-border gap-1 text-center text-white w-full">
                                    <button className="cursor-pointer followed-btn bg-white py-2.5 h-[44px] mq575:h-[35px] px-[22px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                        type="button"
                                        onClick={handleUnFollowClick}
                                    >
                                        <span className="relative text-sm font-body-xs-semi-bold text-purple dark:text-white text-center inline-block">
                                            Followed
                                        </span>
                                        <span>
                                            <img src={CheckIcon} className="w-4 h-4 relative top-[2px]" alt="" />
                                        </span>
                                    </button>
                                </div>
                            ) : (
                                <div className="  grid flex-row items-center justify-center  box-border gap-1 text-center text-white">
                                    <button className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface  flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
                                        type="button"
                                        onClick={handleFollowClick}
                                    >
                                        <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center grid">
                                            Follow
                                        </span>
                                    </button>
                                </div>
                            )}

                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm">
                            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                <InputsNew
                                    contentPlaceholder="User Input"
                                    label="User Input"
                                    type="textarea"
                                    register={register('goalDescription')}
                                    errorMessage={errors.goalDescription}
                                    disabled={true}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <SearchableInputsDropdown
                                label="Project Name*"
                                initialValue={watchProjectName}
                                placeHolder="Select Project"
                                options={projectOptions}
                                onSelect={handleSelectProject}
                                errorMessage={errors?.projectName}
                            />
                            <div className="flex flex-col items-start justify-start box-border gap-[16px] col-span-3">
                                <label className="cursor-pointer self-stretch h-[25px] relative font-semibold inline-block shrink-0">Goal For*</label>
                                <div className="self-stretch flex flex-row items-center justify-start py-0 pl-0 gap-[16px] mq1275:gap-[5px] mq950:gap-[16px] mq450:box-border mq950:box-border mq1425:flex-wrap mq1425:box-border">
                                    <div className="flex flex-row items-center justify-start gap-[6px]">
                                        <input
                                            className="cursor-pointer m-0 h-4 w-4 relative overflow-hidden shrink-0"
                                            type="radio"
                                            value="Customer Success"
                                            {...register('goalFor')}
                                            id="cs"
                                            onClick={() => {
                                                setValue('goalID', '');
                                                setValue('goalType', '');
                                                setClearSelected(true)
                                            }}
                                        />
                                        <label htmlFor="cs" className={`relative inline-block  cursor-pointer`}>
                                            Customer Success
                                        </label>
                                    </div>
                                    <div className="flex flex-row items-center justify-start gap-[6px] ">
                                        <input
                                            className="cursor-pointer m-0 h-4 w-4 relative overflow-hidden shrink-0"
                                            type="radio"
                                            value="Marketing"
                                            {...register('goalFor')}
                                            id="marketing"
                                            onClick={() => {
                                                setValue('goalID', '');
                                                setValue('goalType', '');
                                                setClearSelected(true)
                                            }}
                                        />
                                        <label htmlFor="marketing" className={`relative inline-block  cursor-pointer`}>
                                            Marketing
                                        </label>
                                    </div>
                                    <div className=" flex flex-row items-center justify-start gap-[6px] ">
                                        <input
                                            className="cursor-pointer m-0 h-4 w-4 relative overflow-hidden shrink-0"
                                            type="radio"
                                            value="Product Development"
                                            {...register('goalFor')}
                                            id="pd"
                                            onClick={() => {
                                                setValue('goalID', '');
                                                setValue('goalType', '');
                                                setClearSelected(true)
                                            }}
                                        />
                                        <label htmlFor="pd" className={`relative inline-block cursor-pointer`}>
                                            Product Development
                                        </label>
                                    </div>
                                </div>
                                {errors?.goalFor && (
                                    <p className="text-red p-2 cts-bg-error my-0 flex items-center gap-[4px] ctcerror-width-blg">
                                        <img src={alertIcon} alt="" />
                                        {typeof errors.goalFor.message === 'string' ? errors.goalFor.message : 'Invalid input'}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <InputsDropdownMultipleSearchSelect
                                label="Goal Type*"
                                initialValue={watchGoalType}
                                placeHolder="Select Goal Type"
                                options={goalTypeOptions}
                                onSelect={handleGoalType}
                                inputType="radio"
                                errorMessage={errors?.goalType}
                                clearSelected={clearSelected}
                            />

                            <InputsNew
                                contentPlaceholder="Goal Label/Name*"
                                label="Goal Name"
                                type="text"
                                register={register('goalName')}
                                errorMessage={errors.goalName}
                            />
                        </div>

                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
                            <div className="grid">
                                <InputsDropdownMultipleSearchSelect
                                    label="Skills*"
                                    placeHolder="Choose"
                                    options={skillOptions}
                                    onSelect={handleSelectSkills}
                                    inputType="checkbox"
                                    checkedItems={selectedSkills}
                                />

                                {selectedSkills?.length > 0 && (
                                    <div className="reportmulselect-blg flex-1 w-full cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                                        <div className="inner-reportmulselect-blg items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg mt-2">
                                            {selectedSkills.map((ss: any) => (
                                                <div
                                                    key={ss?.key}
                                                    className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                                                >
                                                    <div className="flex flex-row items-center justify-start">
                                                        <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                                                            {ss?.name}
                                                        </div>
                                                    </div>
                                                    <img
                                                        className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                                                        loading="lazy"
                                                        alt="Remove"
                                                        src={closeDowpDowniconimg}
                                                        onClick={() => handleRemoveSkill(ss.key)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <SearchableInputsDropdown
                                label="Priority*"
                                initialValue={watchPriority}
                                placeHolder="Select Priority"
                                options={priorityOptions}
                                onSelect={handlePriority}
                                errorMessage={errors?.priority}
                            // clearSelected={clearSelected}
                            />

                            <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
                                <Controller
                                    name="expectedEndDate"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="Expected End Date*"
                                            selectedDate={field.value}
                                            onChange={(date: any) => {
                                                field.onChange(date);
                                                setValue('expectedEndDate', date);
                                                trigger('expectedEndDate');
                                            }}
                                            error={errors.expectedEndDate?.message}
                                            nothideParsedValue={true}
                                            minDate={new Date()}
                                        />
                                    )}
                                />
                            </div>
                        </div>



                        <h5 className="my-0 text-xl-dv self-stretch relative text-lg">Knowledge Base</h5>

                        <div className="knowledge_base_section p-4 max-w-full flex flex-col self-stretch ">
                            <div className="w-full overflow-hidden flex flex-row items-center justify-between gap-0 text-lg">

                                <div className="tabsection_goalview w-full relative rounded-md bg-whitesmoke border-gainsboro border-solid border-b-[1px] box-border flex flex-row items-center justify-start p-1 text-center text-base text-dimgray font-manrope">
                                    <div
                                        onClick={() => setIdeaAttachmemntView("attachments")}
                                        className={`cursor-pointer flex-1 rounded flex flex-row items-center justify-center p-4 ${ideaAttachmentView === "attachments" ? 'bg-white' : ''}`}
                                    >
                                        <div className="relative">Attachments ({imgUrls && imgUrls?.length})</div>
                                    </div>
                                    <div
                                        onClick={() => setIdeaAttachmemntView("links")}
                                        className={`cursor-pointer flex-1 shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded flex flex-row items-center justify-center p-4 text-gray ${ideaAttachmentView === "links" ? 'bg-white' : ''} `}
                                    >
                                        <div className="relative font-semibold">Links ({links && links?.length})</div>
                                    </div>
                                </div>
                                {/* <h5 className="my-0 flex-1 relative inline-block max-w-full dark:text-white">
                                        <b className="flex-1 relative whitespace-pre-wrap">Task</b>
                                    </h5> */}

                            </div>
                            <hr className="bdr-custom-taskcrad" />
                            {isUploading && (
                                <Loader />
                            )}

                            {ideaAttachmentView === "attachments" && (
                                <div className="max-w-full flex-col self-stretch grid grid-cols-4 gap-4 mt-6">
                                    <div className="upload-viewidea-box col-span-1 grid text-center gap-[8px] mx-center">
                                        <div className="w-full flex justify-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none" className="relative left-[5px]">
                                                <path d="M11 10.4999V19.4999M11 10.4999L8.49996 12.4999M11 10.4999L13.5 12.4999M4.03396 7.61694C3.08817 7.85494 2.26184 8.4301 1.71021 9.23438C1.15859 10.0387 0.919644 11.0167 1.03827 11.9847C1.15689 12.9528 1.62492 13.8442 2.35443 14.4914C3.08393 15.1387 4.02469 15.4974 4.99996 15.4999H5.99996" stroke="#191D23" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M14.8299 5.63796C14.4881 4.28453 13.6445 3.1117 12.47 2.35722C11.2956 1.60274 9.87819 1.32305 8.50517 1.57487C7.13215 1.82668 5.90625 2.59115 5.07598 3.7133C4.2457 4.83545 3.87318 6.23133 4.03392 7.61796C4.03392 7.61796 4.18692 8.49996 4.49992 8.99996" stroke="#191D23" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M16 15.5C16.706 15.4995 17.404 15.3495 18.0479 15.0599C18.6917 14.7702 19.267 14.3475 19.7357 13.8195C20.2045 13.2915 20.5561 12.6702 20.7674 11.9965C20.9787 11.3229 21.045 10.6121 20.9618 9.91094C20.8786 9.20982 20.6479 8.53427 20.2848 7.92874C19.9217 7.32321 19.4345 6.80145 18.8552 6.39778C18.276 5.99412 17.6178 5.71772 16.924 5.58676C16.2302 5.4558 15.5166 5.47327 14.83 5.638L13.5 6" stroke="#191D23" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                        </div>
                                        <p className="my-0 relative left-[5px]">Upload</p>
                                        <div className="flex justify-center max-w-full relative">
                                            <button
                                                type="button"
                                                onClick={handleIconClick}
                                                className="ml-2 flex items-center justify-center w-8 h-8   cursor-pointer flex flex-col items-start justify-start p-[10px] plus-task-btn taskdash-plush-img rounded"
                                            >
                                                <img
                                                    className="w-4 h-4 relative overflow-hidden shrink-0 cursor-pointer"
                                                    loading="lazy"
                                                    alt=""
                                                    src={ioniconaaddoutline}
                                                />

                                            </button>

                                        </div>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: "none" }}
                                            onChange={handleFileUpload}
                                            // accept={acceptedTypes}
                                            multiple
                                        />
                                    </div>
                                    <div className="upload-viewidea-right col-span-3">
                                        <div className="grid grid-cols-3 mq1400:grid-3 mq1356:grid-2 mq1275:grid-cols-2 mq950:grid-cols-2 mq575:grid-cols-1 gap-[4px] min-w-full">
                                            {imgUrls?.map((url, index) => {
                                                const existing = existingAttachments?.find((att) => att.file_path === url);

                                                return (
                                                    // <div className="upload-viewidea-item gap-[4px] flex items-start relative">
                                                    <a
                                                        key={url}
                                                        href={url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="cursor-pointer upload-viewidea-item gap-[4px] flex items-start relative"
                                                    >
                                                        <div className="upload-viewidea-file  flex justify-center items-center">
                                                            <img src={viewFileIon} alt="" />
                                                        </div>
                                                        <div className="upload-viewidea-content">
                                                            <p className="text-black dark:text-white my-0 font-bold word">{existing?.file_name}</p>
                                                            <p className="my-0 text-black dark:text-white font-normal">{existing?.file_type}</p>
                                                        </div>
                                                        {/* <div className="upload-viewidea-delete flex justify-center items-center absolute right-[8px] top-[8px]">
                                                        <img src={viewdeleteIon} alt="" />
                                                    </div> */}
                                                    </a>
                                                )
                                            })}

                                        </div>
                                    </div>
                                </div>
                            )}

                            {ideaAttachmentView === "links" && (
                                <div className="max-w-full flex-col self-stretch grid grid-cols-4 gap-4 mt-6">
                                    <LinkManager links={links} onLinksChange={setLinks} />
                                </div>
                            )}

                        </div>

                        <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border">
                            <button className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                type="button"
                                onClick={() => navigate('/ideas')}
                            >
                                <div className="relative text-sm font-body-xs-semi-bold dark:text-white text-center inline-block">
                                    Cancel
                                </div>
                            </button>
                            <button className={`cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface  flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue`}
                                type="button"
                                onClick={handleSubmit((data) => onSubmit(data, "update"))}
                            >
                                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                                    Update
                                </div>
                            </button>
                            <button className={`cursor-pointer [border:none] py-2.5 h-[44px] lightblue-bg-item mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px]  flex flex-row items-center justify-center gap-[12px]`}
                                type="button"
                                onClick={handleSubmit((data) => onSubmit(data, "convert"))}

                            >
                                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                                    Convert To Goal
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

            </form >
            {(projectsDataLoading || goalsDataLoading || ideaDataLoading || updateIdeaLoading || createFollowerLoading || deleteFollowerLoading || skillLoading) && (
                <Loader />
            )
            }
        </>
    );
}

export default ViewIdea